import { useState, useEffect, useMemo, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { FileBarChart, Printer, Search, FileText, FileSpreadsheet, FileDown } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';
import FacturasEmitidasModal from './FacturasEmitidasModal';
import DetalleItemsOTModal from './DetalleItemsOTModal';
import FacturasEmitidasSection from './FacturasEmitidasSection';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value);
};

// Iter 294.a: header de columnas compartido (igual a Informe Costo OT)
const CostTableHeader = ({ bgCls = 'bg-muted/50' }) => (
  <TableRow className={`text-xs ${bgCls}`}>
    <TableHead className="py-2 min-w-[180px]">OT / Proyecto / Descripción</TableHead>
    <TableHead className="py-2 w-14">Tipo</TableHead>
    <TableHead className="py-2 text-right">Materiales</TableHead>
    <TableHead className="py-2 text-right">SSTT</TableHead>
    <TableHead className="py-2 text-right">Cant. HH</TableHead>
    <TableHead className="py-2 text-right">Mano Obra</TableHead>
    <TableHead className="py-2 text-right">Gtos. Grales.</TableHead>
    <TableHead className="py-2 text-right bg-slate-200 dark:bg-slate-800">Total Gastos</TableHead>
    <TableHead className="py-2 text-right">Facturado</TableHead>
    <TableHead className="py-2 text-right">Utilidad</TableHead>
  </TableRow>
);

// Fila REAL + PRES de una OT
const OTRows = ({ ot, realBg, onVerItems, onVerFacturas }) => (
  <>
    <TableRow key={`${ot.id}-real`} className={`text-sm ${realBg}`}>
      <TableCell className="py-1 font-mono" rowSpan={2}>
        <span className="font-bold">OT-{ot.numero_ot}</span>
        {ot.esAumentoObra && <span className="text-orange-600 text-xs ml-1">(AO)</span>}
        <span className="block text-xs text-gray-500">{ot.proyecto_codigo}{ot.cotizacion ? ` · Cot. ${ot.cotizacion}` : ''}</span>
        <span className="block text-xs text-gray-600 line-clamp-2">{ot.descripcion}</span>
      </TableCell>
      <TableCell className="py-1 font-semibold text-blue-700 text-xs">REAL</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs">
        <button
          type="button"
          onClick={() => onVerItems(ot, 'MA')}
          className="text-blue-700 hover:text-blue-900 hover:underline"
          title="Ver detalle de materiales"
          data-testid={`btn-items-ma-${ot.numero_ot}`}
        >
          {formatCurrency(ot.real.materiales)}
        </button>
        {ot.real.cantOC > 0 && <span className="block text-blue-600 text-[10px]">({ot.real.cantOC} OC)</span>}
      </TableCell>
      <TableCell className="py-1 text-right font-mono text-xs">
        <button
          type="button"
          onClick={() => onVerItems(ot, 'SE')}
          className="text-amber-700 hover:text-amber-900 hover:underline"
          title="Ver detalle de servicios (SSTT)"
          data-testid={`btn-items-se-${ot.numero_ot}`}
        >
          {formatCurrency(ot.real.sstt)}
        </button>
      </TableCell>
      <TableCell className="py-1 text-right font-mono text-xs">{ot.real.cantHH}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs">{formatCurrency(ot.real.manoObra)}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs">
        <button
          type="button"
          onClick={() => onVerItems(ot, 'GA')}
          className="text-purple-700 hover:text-purple-900 hover:underline"
          title="Ver detalle de gastos generales"
          data-testid={`btn-items-ga-${ot.numero_ot}`}
        >
          {formatCurrency(ot.real.gastosGrales)}
        </button>
      </TableCell>
      <TableCell className="py-1 text-right font-mono text-xs font-semibold bg-slate-100 dark:bg-slate-800">{formatCurrency(ot.real.totalGastos)}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs">
        {ot.real.cantFacturas > 0 ? (
          <button
            type="button"
            onClick={() => onVerFacturas(ot)}
            className="text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
            title="Ver facturas emitidas"
            data-testid={`btn-ver-facturas-${ot.numero_ot}`}
          >
            {formatCurrency(ot.real.facturado)}
            <FileText className="w-3 h-3" />
          </button>
        ) : (
          formatCurrency(ot.real.facturado)
        )}
        {ot.real.cantFacturas > 0 && <span className="block text-blue-600 text-[10px]">({ot.real.cantFacturas} fact)</span>}
      </TableCell>
      <TableCell className={`py-1 text-right font-mono text-xs font-semibold ${ot.real.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {formatCurrency(ot.real.utilidad)}
      </TableCell>
    </TableRow>
    <TableRow key={`${ot.id}-pres`} className="text-sm bg-gray-50 border-b-2">
      <TableCell className="py-1 font-semibold text-gray-500 text-xs">PRES</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.materiales)}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.sstt)}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{ot.pres.cantHH}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.manoObra)}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.gastosGrales)}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs text-gray-600 font-semibold bg-slate-100/60 dark:bg-slate-800/60">{formatCurrency(ot.pres.totalGastos)}</TableCell>
      <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.facturado)}</TableCell>
      <TableCell className={`py-1 text-right font-mono text-xs ${ot.pres.utilidad >= 0 ? 'text-green-500' : 'text-red-500'}`}>
        {formatCurrency(ot.pres.utilidad)}
      </TableCell>
    </TableRow>
  </>
);

// Fila de totales/subtotales
const TotalsRow = ({ label, tipo, tot, rowCls, totalGastosCls, textCls = 'text-white', testid }) => (
  <TableRow className={`${rowCls} ${textCls} font-bold`} data-testid={testid}>
    <TableCell className="py-2">{label}</TableCell>
    <TableCell className="py-2 text-xs">{tipo}</TableCell>
    <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(tot.materiales)}</TableCell>
    <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(tot.sstt)}</TableCell>
    <TableCell className="py-2 text-right font-mono text-xs">{tot.cantHH}</TableCell>
    <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(tot.manoObra)}</TableCell>
    <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(tot.gastosGrales)}</TableCell>
    <TableCell className={`py-2 text-right font-mono text-xs ${totalGastosCls}`}>{formatCurrency(tot.totalGastos)}</TableCell>
    <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(tot.facturado)}</TableCell>
    <TableCell className={`py-2 text-right font-mono text-xs ${tot.utilidad >= 0 ? (textCls === 'text-white' ? 'text-green-300' : 'text-green-700') : (textCls === 'text-white' ? 'text-red-300' : 'text-red-700')}`}>
      {formatCurrency(tot.utilidad)}
    </TableCell>
  </TableRow>
);

// Iter 334: fila de DESVIACIÓN (REAL - PRES) para evaluar presupuesto de un vistazo.
// Gastos: rojo si REAL > PRES (sobre presupuesto). Facturado/Utilidad: verde si REAL >= PRES.
const DesvRow = ({ label, real, pres, testid }) => {
  const d = (k) => (real[k] || 0) - (pres[k] || 0);
  const gastoCls = (v) => (v > 0 ? 'text-red-700' : 'text-green-700');
  const ingresoCls = (v) => (v >= 0 ? 'text-green-700' : 'text-red-700');
  return (
    <TableRow className="bg-amber-100 border-b-4 border-amber-300 font-bold" data-testid={testid}>
      <TableCell className="py-1.5 text-amber-900 text-xs">{label}</TableCell>
      <TableCell className="py-1.5 text-amber-900 text-xs">DESV.</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs ${gastoCls(d('materiales'))}`}>{formatCurrency(d('materiales'))}</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs ${gastoCls(d('sstt'))}`}>{formatCurrency(d('sstt'))}</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs ${gastoCls(d('cantHH'))}`}>{Math.round(d('cantHH') * 100) / 100}</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs ${gastoCls(d('manoObra'))}`}>{formatCurrency(d('manoObra'))}</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs ${gastoCls(d('gastosGrales'))}`}>{formatCurrency(d('gastosGrales'))}</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs bg-amber-200 ${gastoCls(d('totalGastos'))}`}>{formatCurrency(d('totalGastos'))}</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs ${ingresoCls(d('facturado'))}`}>{formatCurrency(d('facturado'))}</TableCell>
      <TableCell className={`py-1.5 text-right font-mono text-xs ${ingresoCls(d('utilidad'))}`}>{formatCurrency(d('utilidad'))}</TableCell>
    </TableRow>
  );
};

const calcTotales = (arr, tipo) => ({
  materiales: arr.reduce((sum, ot) => sum + ot[tipo].materiales, 0),
  sstt: arr.reduce((sum, ot) => sum + ot[tipo].sstt, 0),
  cantHH: Math.round(arr.reduce((sum, ot) => sum + ot[tipo].cantHH, 0) * 100) / 100,
  manoObra: arr.reduce((sum, ot) => sum + ot[tipo].manoObra, 0),
  gastosGrales: arr.reduce((sum, ot) => sum + ot[tipo].gastosGrales, 0),
  totalGastos: arr.reduce((sum, ot) => sum + ot[tipo].totalGastos, 0),
  total: arr.reduce((sum, ot) => sum + ot[tipo].total, 0),
  facturado: arr.reduce((sum, ot) => sum + ot[tipo].facturado, 0),
  utilidad: arr.reduce((sum, ot) => sum + ot[tipo].utilidad, 0),
});

export default function InformeProyecto() {
  const [proyectos, setProyectos] = useState([]);
  const [allOTs, setAllOTs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Iter 334 (Renzo): multi-proyecto, por defecto sólo los "en ejecución"
  const [soloEnEjecucion, setSoloEnEjecucion] = useState(true);
  const [selectedProyectos, setSelectedProyectos] = useState(new Set());
  const [selectedOTs, setSelectedOTs] = useState(new Set());
  const [reportData, setReportData] = useState(null);
  const [selectedFacturasOT, setSelectedFacturasOT] = useState(null);
  const [itemsModal, setItemsModal] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [proyRes, otsRes] = await Promise.all([
          api.get('/proyectos'),
          api.get('/ordenes-trabajo'),
        ]);
        const sorted = proyRes.data.sort((a, b) => {
          const clienteA = (a.cliente_nombre || '').toLowerCase();
          const clienteB = (b.cliente_nombre || '').toLowerCase();
          if (clienteA !== clienteB) return clienteA.localeCompare(clienteB);
          return (a.codigo || '').localeCompare(b.codigo || '');
        });
        setProyectos(sorted);
        setAllOTs(otsRes.data);
      } catch (_error) {
        toast.error('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const otsPorProyecto = useMemo(() => {
    const m = {};
    allOTs.forEach(ot => {
      if (!ot.proyecto_id) return;
      (m[ot.proyecto_id] ||= []).push(ot);
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => a.numero_ot - b.numero_ot));
    return m;
  }, [allOTs]);

  const proyectosEnEjecucion = useMemo(
    () => new Set(allOTs.filter(o => o.estado === 'EN EJECUCION' && o.proyecto_id).map(o => o.proyecto_id)),
    [allOTs]
  );

  const visibleProyectos = proyectos.filter(p => {
    if (soloEnEjecucion && !proyectosEnEjecucion.has(p.id)) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      p.codigo?.toLowerCase().includes(search) ||
      p.descripcion?.toLowerCase().includes(search) ||
      p.cliente_nombre?.toLowerCase().includes(search)
    );
  });

  // OTs disponibles de los proyectos seleccionados (orden proyecto → OT)
  const otsDisponibles = useMemo(() => {
    const pids = proyectos.filter(p => selectedProyectos.has(p.id)).map(p => p.id);
    return pids.flatMap(pid => otsPorProyecto[pid] || []);
  }, [selectedProyectos, otsPorProyecto, proyectos]);

  // Al cambiar la selección de proyectos: pre-marcar sólo las OTs EN EJECUCION
  useEffect(() => {
    setReportData(null);
    const enEjec = otsDisponibles.filter(o => o.estado === 'EN EJECUCION');
    setSelectedOTs(new Set((enEjec.length > 0 ? enEjec : otsDisponibles).map(o => o.numero_ot)));
  }, [otsDisponibles]);

  const toggleProyecto = (pid, checked) => {
    setSelectedProyectos(prev => {
      const next = new Set(prev);
      if (checked) next.add(pid);
      else next.delete(pid);
      return next;
    });
  };

  const toggleOT = (numeroOT, checked) => {
    setSelectedOTs(prev => {
      const next = new Set(prev);
      if (checked) next.add(numeroOT);
      else next.delete(numeroOT);
      return next;
    });
  };

  const loadReportData = async () => {
    const ots = otsDisponibles.filter(ot => selectedOTs.has(ot.numero_ot));
    if (ots.length === 0) {
      toast.error('Seleccione al menos un proyecto y una OT para el informe');
      return;
    }

    setGenerating(true);
    try {
      const costosRealesPromises = ots.map(async (ot) => {
        try {
          const res = await api.get(`/compras/costo-materiales-por-ot?ot_numero=${ot.numero_ot}`);
          return {
            numero_ot: ot.numero_ot,
            materiales: res.data.total_materiales || 0,
            sstt: res.data.total_sstt || 0,
            gastos_generales: res.data.total_gastos_generales || 0,
            cantOC: res.data.cantidad_ocs || 0
          };
        } catch {
          return { numero_ot: ot.numero_ot, materiales: 0, sstt: 0, gastos_generales: 0, cantOC: 0 };
        }
      });
      const hhPromises = ots.map(async (ot) => {
        try {
          const res = await api.get(`/control-hh/resumen-ot/${ot.numero_ot}`);
          return { numero_ot: ot.numero_ot, total_horas: Number(res.data.total_horas) || 0 };
        } catch {
          return { numero_ot: ot.numero_ot, total_horas: 0 };
        }
      });
      const facturadoPromises = ots.map(async (ot) => {
        try {
          const res = await api.get(`/ventas/por-ot/${ot.numero_ot}`);
          return {
            numero_ot: ot.numero_ot,
            neto: Number(res.data.total_facturado_neto) || 0,
            cantidad: Number(res.data.cantidad_facturas) || 0,
            ventas: res.data.ventas || [],
          };
        } catch {
          return { numero_ot: ot.numero_ot, neto: 0, cantidad: 0, ventas: [] };
        }
      });

      const [costosRealesArray, hhArray, facturadoArray] = await Promise.all([
        Promise.all(costosRealesPromises),
        Promise.all(hhPromises),
        Promise.all(facturadoPromises),
      ]);
      const costosRealesMap = Object.fromEntries(costosRealesArray.map(c => [c.numero_ot, c]));
      const hhMap = Object.fromEntries(hhArray.map(h => [h.numero_ot, h]));
      const facturadoMap = Object.fromEntries(facturadoArray.map(f => [f.numero_ot, f]));

      const otData = ots.map(ot => {
        const pres = ot.presupuesto || {};
        const costosReales = costosRealesMap[ot.numero_ot] || { materiales: 0, cantOC: 0 };

        const presMateriales = pres.materiales || 0;
        const presSSTT = pres.servicios_terceros || 0;
        const presCantHH = pres.numero_hh || 0;
        const presValorHH = pres.valor_hh || 12500;
        const presManoObra = presCantHH * presValorHH;
        const presGastosGrales = pres.gastos_generales || 0;
        const presTotal = presMateriales + presSSTT + presManoObra + presGastosGrales;
        const presFacturado = pres.total || 0;
        const presUtilidad = presFacturado - presTotal;

        const realMateriales = costosReales.materiales;
        const realSSTT = costosReales.sstt || 0;
        const realCantHH = (hhMap[ot.numero_ot] || {}).total_horas || 0;
        const realValorHH = pres.valor_hh || 12500;
        const realManoObra = realCantHH * realValorHH;
        const realGastosGrales = costosReales.gastos_generales || 0;
        const realTotalGastos = realMateriales + realSSTT + realManoObra + realGastosGrales;
        const realFacturado = (facturadoMap[ot.numero_ot] || {}).neto || 0;
        const realUtilidad = realFacturado - realTotalGastos;

        return {
          ...ot,
          real: {
            materiales: realMateriales,
            sstt: realSSTT,
            cantHH: realCantHH,
            valorHH: realValorHH,
            manoObra: realManoObra,
            gastosGrales: realGastosGrales,
            totalGastos: realTotalGastos,
            total: realTotalGastos,
            facturado: realFacturado,
            cantFacturas: (facturadoMap[ot.numero_ot] || {}).cantidad || 0,
            ventasList: (facturadoMap[ot.numero_ot] || {}).ventas || [],
            utilidad: realUtilidad,
            cantOC: costosReales.cantOC
          },
          pres: {
            materiales: presMateriales,
            sstt: presSSTT,
            cantHH: presCantHH,
            valorHH: presValorHH,
            manoObra: presManoObra,
            gastosGrales: presGastosGrales,
            totalGastos: presTotal,
            total: presTotal,
            facturado: presFacturado,
            utilidad: presUtilidad
          },
          esAumentoObra: ot.es_aumento_obra || false
        };
      });

      // Iter 334: bloque por proyecto → grupos por cotización (sub-subtotal si 2+ OTs)
      const proyectosSel = proyectos.filter(p => selectedProyectos.has(p.id));
      const bloques = proyectosSel
        .map(p => {
          const otsP = otData.filter(o => o.proyecto_id === p.id);
          if (otsP.length === 0) return null;
          const gruposMap = new Map();
          otsP.forEach(o => {
            const raw = String(o.cotizacion || '').trim();
            const cot = (!raw || raw === '0') ? 'SIN COTIZACIÓN' : raw;
            if (!gruposMap.has(cot)) gruposMap.set(cot, []);
            gruposMap.get(cot).push(o);
          });
          const grupos = [...gruposMap.entries()].map(([cotizacion, arr]) => ({
            cotizacion,
            ots: arr,
            totalesReal: calcTotales(arr, 'real'),
            totalesPres: calcTotales(arr, 'pres'),
          }));
          return {
            proyecto: p,
            ots: otsP,
            grupos,
            totalesReal: calcTotales(otsP, 'real'),
            totalesPres: calcTotales(otsP, 'pres'),
          };
        })
        .filter(Boolean);

      setReportData({
        bloques,
        otData,
        otsSeleccionadas: ots.map(o => o.numero_ot),
        totalesReal: calcTotales(otData, 'real'),
        totalesPres: calcTotales(otData, 'pres'),
      });
    } catch (_error) {
      toast.error('Error al generar informe');
    } finally {
      setGenerating(false);
    }
  };

  // Exportes por proyecto (multi: descarga un archivo por proyecto)
  const handleExportExcel = async () => {
    if (!reportData) return;
    setExporting(true);
    try {
      for (const bloque of reportData.bloques) {
        const otsParam = bloque.ots.map(o => o.numero_ot).join(',');
        const res = await api.get(`/informes/proyecto-excel/${bloque.proyecto.id}${otsParam ? `?ots=${otsParam}` : ''}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Informe_Proyecto_${bloque.proyecto.codigo}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success(`Excel descargado (${reportData.bloques.length} proyecto(s))`);
    } catch {
      toast.error('Error al generar el Excel del proyecto');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!reportData) return;
    setExportingPdf(true);
    try {
      for (const bloque of reportData.bloques) {
        const otsParam = bloque.ots.map(o => o.numero_ot).join(',');
        const res = await api.get(`/informes/proyecto-pdf/${bloque.proyecto.id}${otsParam ? `?ots=${otsParam}` : ''}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Informe_Proyecto_${bloque.proyecto.codigo}_${new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success(`PDF descargado (${reportData.bloques.length} proyecto(s))`);
    } catch {
      toast.error('Error al generar el PDF del proyecto');
    } finally {
      setExportingPdf(false);
    }
  };

  const handlePrint = () => window.print();
  const handleVerItems = (ot, tipo) => setItemsModal({ ot, tipo });
  const handleVerFacturas = (ot) => setSelectedFacturasOT(ot);

  if (loading && proyectos.length === 0) {
    return <Skeleton className="h-96" />;
  }

  return (
    <div className="space-y-4" data-testid="informe-proyecto-page">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <FileBarChart className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Informe por Proyecto</h1>
            <p className="text-muted-foreground text-sm">Costos, HH y desviaciones de presupuesto — uno o varios proyectos</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por código, descripción o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none shrink-0" data-testid="toggle-solo-en-ejecucion">
                <Checkbox
                  checked={soloEnEjecucion}
                  onCheckedChange={(v) => setSoloEnEjecucion(!!v)}
                />
                Sólo proyectos con OTs en ejecución
              </label>
            </div>

            {/* Iter 334: selección múltiple de proyectos */}
            <div className="border rounded-md p-3 bg-muted/30" data-testid="proyecto-selector">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <label className="text-sm font-medium">
                  Proyectos ({selectedProyectos.size} de {visibleProyectos.length} seleccionados)
                </label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedProyectos(new Set(visibleProyectos.map(p => p.id)))}
                    data-testid="btn-marcar-todos-proyectos"
                  >
                    Todos
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedProyectos(new Set())}
                    data-testid="btn-desmarcar-todos-proyectos"
                  >
                    Ninguno
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-y-auto">
                {visibleProyectos.map(p => (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleProyecto(p.id, !selectedProyectos.has(p.id))}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleProyecto(p.id, !selectedProyectos.has(p.id)); }}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted rounded px-1 py-1"
                  >
                    <Checkbox
                      checked={selectedProyectos.has(p.id)}
                      className="pointer-events-none"
                      data-testid={`chk-proyecto-${p.codigo}`}
                    />
                    <span className="font-mono font-semibold shrink-0">{p.codigo}</span>
                    {proyectosEnEjecucion.has(p.id) && (
                      <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 text-[10px] shrink-0">EN EJEC.</span>
                    )}
                    <span className="truncate text-muted-foreground">{p.cliente_nombre || 'Sin Cliente'} · {p.descripcion}</span>
                  </div>
                ))}
                {visibleProyectos.length === 0 && (
                  <p className="text-xs text-muted-foreground py-2">Sin proyectos para el filtro actual.</p>
                )}
              </div>
            </div>

            {/* Selección de OTs a incluir */}
            {otsDisponibles.length > 0 && (
              <div className="border rounded-md p-3 bg-muted/30" data-testid="ot-selector">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <label className="text-sm font-medium">
                    OTs a incluir ({selectedOTs.size} de {otsDisponibles.length}) — por defecto sólo EN EJECUCION
                  </label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedOTs(new Set(otsDisponibles.map(o => o.numero_ot)))}
                      data-testid="btn-marcar-todas-ots"
                    >
                      Todas
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedOTs(new Set(otsDisponibles.filter(o => o.estado === 'EN EJECUCION').map(o => o.numero_ot)))}
                      data-testid="btn-solo-ejecucion-ots"
                    >
                      Sólo en ejecución
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedOTs(new Set())}
                      data-testid="btn-desmarcar-todas-ots"
                    >
                      Ninguna
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-48 overflow-y-auto">
                  {otsDisponibles.map(ot => (
                    <div
                      key={ot.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleOT(ot.numero_ot, !selectedOTs.has(ot.numero_ot))}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleOT(ot.numero_ot, !selectedOTs.has(ot.numero_ot)); }}
                      className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted rounded px-1 py-1"
                    >
                      <Checkbox
                        checked={selectedOTs.has(ot.numero_ot)}
                        className="pointer-events-none"
                        data-testid={`chk-ot-${ot.numero_ot}`}
                      />
                      <span className="font-mono font-semibold shrink-0">OT-{ot.numero_ot}</span>
                      <span className="text-gray-400 text-[10px] shrink-0">{ot.proyecto_codigo}</span>
                      {ot.es_aumento_obra && <span className="text-orange-600 text-[10px] shrink-0">(AO)</span>}
                      {ot.estado === 'EN EJECUCION' && <span className="text-emerald-600 text-[10px] shrink-0">●</span>}
                      <span className="truncate text-muted-foreground">{ot.descripcion}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={loadReportData} disabled={generating || selectedOTs.size === 0} data-testid="btn-generar-informe">
                {generating ? 'Generando...' : 'Generar Informe'}
              </Button>
              {reportData && (
                <>
                  <Button
                    onClick={handleExportExcel}
                    disabled={exporting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="btn-exportar-excel-proyecto"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {exporting ? 'Generando...' : 'Exportar Excel'}
                  </Button>
                  <Button
                    onClick={handleExportPDF}
                    disabled={exportingPdf}
                    className="bg-red-700 hover:bg-red-800"
                    data-testid="btn-exportar-pdf-proyecto"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    {exportingPdf ? 'Generando...' : 'Exportar PDF'}
                  </Button>
                  <Button variant="outline" onClick={handlePrint}>
                    <Printer className="h-4 w-4 mr-2" />
                    Imprimir
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report */}
      {reportData && (
        <>
          {/* Print Header */}
          <div className="hidden print:block mb-4">
            <div className="text-center border-b-2 border-black pb-2">
              <h1 className="text-xl font-bold">CHIRINO STEEL</h1>
              <h2 className="text-lg">Informe por Proyecto: {reportData.bloques.map(b => b.proyecto.codigo).join(', ')}</h2>
              <p className="text-sm">OTs incluidas: {reportData.otsSeleccionadas?.length}</p>
              <p className="text-sm text-gray-600">Fecha: {new Date().toLocaleDateString('es-CL')}</p>
            </div>
          </div>

          {/* Iter 334: un bloque por proyecto con encabezado destacado */}
          {reportData.bloques.map(bloque => (
            <Card key={bloque.proyecto.id} className="border-2 border-blue-700 overflow-hidden" data-testid={`bloque-proyecto-${bloque.proyecto.codigo}`}>
              <CardHeader className="py-2.5 bg-blue-700">
                <CardTitle className="text-base text-white flex flex-wrap items-baseline gap-2">
                  <span className="uppercase tracking-widest text-xs bg-blue-900 rounded px-2 py-0.5">Proyecto</span>
                  <span className="font-mono">{bloque.proyecto.codigo}</span>
                  <span className="font-normal">— {bloque.proyecto.descripcion}</span>
                  <span className="text-blue-200 text-xs">{bloque.proyecto.cliente_nombre || 'Sin Cliente'} · {bloque.ots.length} OT(s)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <CostTableHeader />
                    </TableHeader>
                    <TableBody>
                      {bloque.grupos.map(grupo => (
                        <Fragment key={grupo.cotizacion}>
                          {grupo.ots.map(ot => (
                            <OTRows
                              key={ot.id}
                              ot={ot}
                              realBg={ot.esAumentoObra ? 'bg-orange-50/50' : 'bg-blue-50/50'}
                              onVerItems={handleVerItems}
                              onVerFacturas={handleVerFacturas}
                            />
                          ))}
                          {/* Sub-subtotal por cotización cuando agrupa 2+ OTs con cotización real */}
                          {grupo.ots.length > 1 && grupo.cotizacion !== 'SIN COTIZACIÓN' && (
                            <>
                              <TotalsRow
                                label={`Subtotal Cot. ${grupo.cotizacion} (${grupo.ots.length} OTs)`}
                                tipo="REAL"
                                tot={grupo.totalesReal}
                                rowCls="bg-sky-200"
                                totalGastosCls="bg-sky-300"
                                textCls="text-sky-900"
                                testid={`subtotal-cot-${bloque.proyecto.codigo}-${grupo.cotizacion}`}
                              />
                              <TotalsRow
                                label=""
                                tipo="PRES"
                                tot={grupo.totalesPres}
                                rowCls="bg-sky-100"
                                totalGastosCls="bg-sky-200"
                                textCls="text-sky-800"
                              />
                            </>
                          )}
                        </Fragment>
                      ))}
                      {/* Subtotal del proyecto + desviación */}
                      <TotalsRow
                        label={`SUBTOTAL PROYECTO ${bloque.proyecto.codigo}`}
                        tipo="REAL"
                        tot={bloque.totalesReal}
                        rowCls="bg-blue-800"
                        totalGastosCls="bg-blue-900"
                        testid={`subtotal-proyecto-${bloque.proyecto.codigo}`}
                      />
                      <TotalsRow label="" tipo="PRES" tot={bloque.totalesPres} rowCls="bg-gray-600" totalGastosCls="bg-gray-700" />
                      <DesvRow
                        label="DESVIACIÓN (Real - Pres)"
                        real={bloque.totalesReal}
                        pres={bloque.totalesPres}
                        testid={`desv-proyecto-${bloque.proyecto.codigo}`}
                      />
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Total General */}
          {reportData.bloques.length > 1 && (
            <Card className="border-2 border-gray-800">
              <CardHeader className="py-2">
                <CardTitle className="text-lg">TOTAL GENERAL ({reportData.bloques.length} proyectos)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableBody>
                      <TotalsRow label="TOTAL" tipo="REAL" tot={reportData.totalesReal} rowCls="bg-gray-800" totalGastosCls="bg-gray-900" testid="total-general-real" />
                      <TotalsRow label="" tipo="PRES" tot={reportData.totalesPres} rowCls="bg-gray-600" totalGastosCls="bg-gray-700" />
                      <DesvRow label="DESVIACIÓN (Real - Pres)" real={reportData.totalesReal} pres={reportData.totalesPres} testid="desv-total-general" />
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:hidden">
            <Card className="border-blue-200">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Utilidad REAL</p>
                <p className={`text-xl font-bold ${reportData.totalesReal.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.totalesReal.utilidad)}
                </p>
                <p className="text-xs text-muted-foreground">Facturado - Total Gastos</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Utilidad PRES</p>
                <p className={`text-xl font-bold ${reportData.totalesPres.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.totalesPres.utilidad)}
                </p>
                <p className="text-xs text-muted-foreground">Presupuesto original</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Desviación Gastos</p>
                <p className={`text-xl font-bold ${(reportData.totalesReal.totalGastos - reportData.totalesPres.totalGastos) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(reportData.totalesReal.totalGastos - reportData.totalesPres.totalGastos)}
                </p>
                <p className="text-xs text-muted-foreground">Real - Presupuestado</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Margen Real</p>
                <p className={`text-xl font-bold ${reportData.totalesReal.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {reportData.totalesReal.facturado > 0
                    ? `${((reportData.totalesReal.utilidad / reportData.totalesReal.facturado) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Facturas emitidas de todas las OTs incluidas */}
          <FacturasEmitidasSection ots={reportData.otData} />
        </>
      )}

      {/* Modales compartidos con Informe Costo OT */}
      <FacturasEmitidasModal ot={selectedFacturasOT} onClose={() => setSelectedFacturasOT(null)} />
      <DetalleItemsOTModal
        ot={itemsModal?.ot || null}
        tipoInicial={itemsModal?.tipo}
        onClose={() => setItemsModal(null)}
      />
    </div>
  );
}
